
import { Component, Prop, Watch } from "vue-property-decorator";
import RevisionsList from "@/components/Reusable/Revisions/RevisionsList.vue";
import Vue from "vue";
import { RevisionCommonData, RevisionUiItem } from "@/types/revisions";
import { globalStore, uiStore } from "@/store/store-accessor";
import { getModuleNameFromMutation } from "@/store/util";
import defensiveCopy from "@/util/defensive-copy";
import { isEqual } from "lodash";
import { breakdanceUndo } from "@/lib/undo/undo";
import GlobalModule from "@/store/modules/global";

@Component({
  components: { RevisionsList }
})
export default class RevisionsPanel<T extends RevisionCommonData> extends Vue {
  @Prop({ required: true }) resourceKey!: keyof T;
  @Prop({ required: true }) restoreMethod!: keyof GlobalModule;
  @Prop({ required: true }) updateMethod!: keyof GlobalModule;
  @Prop({ required: true }) fetchRevisionsMethod!: () => Promise<T[]>;
  @Prop({ required: true }) resources!: T[];
  @Prop({ required: true }) resourceHasUnsavedChangesPresent!: boolean;

  isLoading = false;
  remoteRevisions: T[] | null = null;
  selectedIndex: number | null = null;
  unsubscribeFromStore = () => {};

  currentUnsavedRevision: T | null = null;

  @Watch("unsavedChangesPresent")
  async updateRevisionsAfterSaving() {
    if (!this.unsavedChangesPresent) {
      await this.loadRemoteRevisions();
      this.currentUnsavedRevision = null;
      this.recalculateSelectedIndex();
    }
  }

  get unsavedChangesPresent() {
    return uiStore.unsavedChangesPresent;
  }

  get revisionUiItems(): RevisionUiItem<T>[] {
    return this.revisions.map(
      (revision: T, index: number): RevisionUiItem<T> => ({
        isSelected: index === this.selectedIndex,
        revisionData: revision
      })
    );
  }

  get revisions(): T[] {
    const remoteRevisions = this.remoteRevisions ?? [];

    return this.currentUnsavedRevision
      ? [this.currentUnsavedRevision, ...remoteRevisions]
      : remoteRevisions;
  }

  async beforeMount() {
    await this.loadRemoteRevisions();

    if (this.resourceHasUnsavedChangesPresent) {
      this.recalculateSelectedIndex();

      /**
       * Without this condition, opening revisions list after Undoing/Redoing
       * revision selection will contain "Current" revision
       */
      if (this.selectedIndex === -1) {
        this.updateUnsavedRevisionIfNeeded();
      }
    }

    this.recalculateSelectedIndex();

    this.unsubscribeFromStore = this.$store.subscribe(mutationPayload => {
      const moduleName = getModuleNameFromMutation(mutationPayload);

      if (
        moduleName === "global" &&
        mutationPayload.type !== `global/${this.restoreMethod}` &&
        mutationPayload.type !== "global/setGlobalSettings" // should be ignored
      ) {
        // Undo/Redo
        if (mutationPayload.type === `global/${this.updateMethod}`) {
          this.currentUnsavedRevision = null;
        } else {
          this.updateUnsavedRevisionIfNeeded();
        }

        this.recalculateSelectedIndex();
      }
    });
  }

  beforeDestroy() {
    this.unsubscribeFromStore();
  }

  async loadRemoteRevisions() {
    this.isLoading = true;
    this.remoteRevisions = await this.fetchRevisionsMethod();
    this.selectedIndex = null;
    this.isLoading = false;
  }

  recalculateSelectedIndex() {
    const currentResources = defensiveCopy(this.resources);

    this.selectedIndex = this.revisions.findIndex(revision => {
      return isEqual(revision[this.resourceKey], currentResources);
    });
  }

  updateUnsavedRevisionIfNeeded() {
    if (this.hasCurrentStoreDataChanged()) {
      this.currentUnsavedRevision = null;
    } else {
      this.setUnsavedRevisionFromStore();
    }
  }

  hasCurrentStoreDataChanged() {
    if (this.remoteRevisions === null) {
      return false;
    }

    const [mostRecentRemoteRevision] = this.remoteRevisions;
    const currentResources = defensiveCopy(this.resources);

    return isEqual(
      mostRecentRemoteRevision[this.resourceKey],
      currentResources
    );
  }

  setUnsavedRevisionFromStore() {
    this.currentUnsavedRevision = {
      id: -1,
      author: false,
      date: "Current",
      [this.resourceKey]: defensiveCopy(this.resources)
    } as T;
  }

  onRevisionSelected(revisionUiItem: RevisionUiItem<T>, index: number) {
    this.selectedIndex = index;

    breakdanceUndo.transaction(
      () => globalStore[this.restoreMethod](revisionUiItem.revisionData),
      `Restore ${this.resourceKey} from revision`
    );
  }
}
